/**
 * Message Parser Utilities
 * 
 * Provides functions to parse chat messages and transform text content.
 * Currently supports:
 * - URL detection and conversion to clickable links
 * 
 * Can be expanded to support:
 * - Mentions (@username)
 * - Hashtags (#topic)
 * - Emoji conversion
 * - Custom markdown
 */

/**
 * URL regex pattern that matches http, https, and www URLs
 * Captures the full URL including query parameters and fragments
 */
const URL_REGEX = /(https?:\/\/[^\s]+)|(www\.[^\s]+)/gi;

/**
 * Parse text and convert URLs to clickable links
 * 
 * @param {string} text - The text to parse
 * @returns {Array} Array of text segments and link objects
 * 
 * @example
 * const parsed = parseUrls("Check out https://example.com for more info");
 * // Returns: [
 * //   { type: 'text', content: 'Check out ' },
 * //   { type: 'link', url: 'https://example.com', display: 'https://example.com' },
 * //   { type: 'text', content: ' for more info' }
 * // ]
 */
export function parseUrls(text) {
  if (!text || typeof text !== 'string') {
    return [{ type: 'text', content: text || '' }];
  }

  const parts = [];
  let lastIndex = 0;
  let match;

  // Reset regex lastIndex
  URL_REGEX.lastIndex = 0;

  while ((match = URL_REGEX.exec(text)) !== null) {
    const url = match[0];
    const matchIndex = match.index;

    // Add text before the URL
    if (matchIndex > lastIndex) {
      parts.push({
        type: 'text',
        content: text.substring(lastIndex, matchIndex)
      });
    }

    // Determine the full URL (add https:// if it starts with www)
    let fullUrl = url;
    if (url.startsWith('www.')) {
      fullUrl = `https://${url}`;
    }

    // Add the URL as a link
    parts.push({
      type: 'link',
      url: fullUrl,
      display: url
    });

    lastIndex = matchIndex + url.length;
  }

  // Add remaining text after the last URL
  if (lastIndex < text.length) {
    parts.push({
      type: 'text',
      content: text.substring(lastIndex)
    });
  }

  // If no URLs were found, return the original text
  if (parts.length === 0) {
    parts.push({
      type: 'text',
      content: text
    });
  }

  return parts;
}

/**
 * Main message parser that applies all parsing rules
 * 
 * @param {string} text - The message text to parse
 * @returns {Array} Array of parsed segments
 * 
 * @example
 * const parsed = parseMessage("Visit https://example.com");
 * // Returns array of text and link segments
 */
export function parseMessage(text) {
  // Currently only parses URLs
  // Can be extended to chain multiple parsers:
  // return parseMentions(parseHashtags(parseUrls(text)));
  return parseUrls(text);
}

/**
 * Check if a string contains any URLs
 * 
 * @param {string} text - The text to check
 * @returns {boolean} True if text contains URLs
 */
export function containsUrls(text) {
  if (!text || typeof text !== 'string') {
    return false;
  }
  URL_REGEX.lastIndex = 0;
  return URL_REGEX.test(text);
}
